import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  AppBar,
  Toolbar,
  Typography,
  Menu,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  MisaOutward,
  MisaOutwardDetail,
  MisaInward,
  MisaInwardDetail,
  MisaPurchase,
  MisaPurchaseDetail,
  DeliveryOrder,
} from "./types/misa";
import { printPhieuXuat } from "./services/printService";
import { printPhieuNhapKho } from "./services/printServiceNhapKho";
import { printServiceNhapHang } from "./services/printServiceNhapHang";
import { exportPDFService } from "./services/pdfExportService";
import { exportExcelService } from "./services/excelExportService";
import { exportExcelServiceNhapKho } from "./services/excelExportServiceNhapKho";
import { exportDeliveryOrdersToExcel } from "./services/excelExportDeliveryOrders";
import {
  fetchMisaToken,
  fetchMisaData,
  fetchMisaDetails,
  fetchMisaDataNhapKho,
  fetchMisaInwardDetails,
  fetchMisaDataNhapHang,
  fetchMisaPurchaseDetails,
  fetchInventory,
  fetchDeliveryOrders,
} from "./services/api";
import { BRANCHES } from "./config/branches";
import {
  Account,
  getAccountSession,
  clearAccountSession,
  isAdmin,
  getAccessibleBranches,
} from "./config/accounts";
import LoginForm from "./components/LoginForm";
import type { InventoryItem } from "./types/misa";
import "./App.css";

interface ColumnDef {
  id: keyof MisaOutward | "Action";
  label: string;
  minWidth: number;
  align?: "left" | "right" | "center";
}

const misaColumns: ColumnDef[] = [
  { id: "RefDate", label: "Ngày", minWidth: 80, align: "center" },
  { id: "RefNo", label: "Số phiếu xuất", minWidth: 80, align: "left" },
  { id: "TotalAmount", label: "Tổng tiền", minWidth: 80, align: "center" },
  { id: "JournalMemo", label: "Diễn giải", minWidth: 300, align: "left" },
  { id: "Action", label: "Thao tác", minWidth: 150, align: "center" },
];

const App: React.FC = () => {
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs());
  const [toDate, setToDate] = useState<Dayjs>(dayjs());
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [misaData, setMisaData] = useState<
    (MisaOutward | MisaInward | MisaPurchase)[]
  >([]);
  const [misaToken, setMisaToken] = useState<string>("");
  const [searchRefNo, setSearchRefNo] = useState<string>("");
  const [searchJournalMemo, setSearchJournalMemo] = useState<string>("");
  const [loaiPhieu, setLoaiPhieu] = useState<"xuat" | "nhapkho" | "nhaphang">(
    "xuat",
  );
  // mặc định chọn chi nhánh Kho tổng
  const [selectedBranch, setSelectedBranch] = useState<string>(
    "66847e41-5b66-4529-b955-e63c7e8b50b0",
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Check if user is already logged in
  useEffect(() => {
    const session = getAccountSession();
    if (session) {
      setCurrentAccount(session);
      // Set default branch based on account
      if (!isAdmin(session) && session.branchId) {
        setSelectedBranch(session.branchId);
      }
    }
  }, []);

  const handleLoginSuccess = (account: Account) => {
    setCurrentAccount(account);
    if (!isAdmin(account) && account.branchId) {
      setSelectedBranch(account.branchId);
    }
  };

  const handleLogout = () => {
    clearAccountSession();
    setCurrentAccount(null);
    setAccountMenuAnchor(null);
    setMisaData([]);
    setSearchRefNo("");
    setSearchJournalMemo("");
  };

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  // Get accessible branches for current user
  const accessibleBranches = currentAccount
    ? getAccessibleBranches(currentAccount)
    : [];
  // Admin sees all branches, User sees only accessible ones
  const filteredBranches =
    currentAccount && isAdmin(currentAccount)
      ? BRANCHES
      : BRANCHES.filter((branch) => accessibleBranches.includes(branch.id));

  const handleGetMisaData = useCallback(async () => {
    const formattedFromDate = fromDate?.format("YYYY-MM-DD");
    const formattedToDate = toDate?.format("YYYY-MM-DD");
    setIsLoading(true);
    try {
      if (loaiPhieu === "xuat") {
        const data = await fetchMisaData(
          misaToken,
          formattedFromDate,
          formattedToDate,
          selectedBranch,
        );
        setMisaData(data);
      } else if (loaiPhieu === "nhapkho") {
        const data = await fetchMisaDataNhapKho(
          misaToken,
          formattedFromDate,
          formattedToDate,
          selectedBranch,
        );
        setMisaData(data);
      } else if (loaiPhieu === "nhaphang") {
        const data = await fetchMisaDataNhapHang(
          misaToken,
          formattedFromDate,
          formattedToDate,
          selectedBranch,
        );
        setMisaData(data);
      }
      // call API fetchInventory và lưu vào state
      const inventoryResponse = await fetchInventory(misaToken, {
        page: 1,
        limit: 10000,
      });
      console.log("Fetched inventory:", inventoryResponse);
      setInventorries(inventoryResponse.data ?? []);
    } catch (error) {
      console.error("Lỗi khi gọi MISA API:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, misaToken, loaiPhieu, selectedBranch]);

  React.useEffect(() => {
    const fetchToken = async () => {
      const token = await fetchMisaToken();
      if (token) setMisaToken(token);
    };
    fetchToken();
  }, []);

  React.useEffect(() => {
    if (misaToken) handleGetMisaData();
  }, [misaToken, handleGetMisaData]);

  const handleFromDateChange = (newValue: Dayjs | null) =>
    newValue && setFromDate(newValue);

  const handleToDateChange = (newValue: Dayjs | null) =>
    newValue && setToDate(newValue);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const fetchDetails = async (
    refId: string,
  ): Promise<
    MisaOutwardDetail[] | MisaInwardDetail[] | MisaPurchaseDetail[]
  > => {
    if (loaiPhieu === "xuat") {
      return await fetchMisaDetails(misaToken, refId, selectedBranch);
    } else if (loaiPhieu === "nhapkho") {
      return await fetchMisaInwardDetails(misaToken, refId, selectedBranch);
    } else {
      return await fetchMisaPurchaseDetails(misaToken, refId, selectedBranch);
    }
  };

  // const handleExportSingleRow = async (row: MisaOutward) => {
  //   if (!row?.RefID) return alert("Không thể lấy chi tiết phiếu - thiếu RefID");
  //   try {
  //     const details = await fetchDetails(row.RefID);
  //     exportPhieuXuatToExcel(row, details);
  //   } catch (e) {
  //     console.error("Lỗi khi lấy chi tiết phiếu:", e);
  //     alert("Có lỗi xảy ra khi lấy chi tiết phiếu. Vui lòng thử lại!");
  //   }
  // };

  const handlePrint = async (row: MisaOutward | MisaInward | MisaPurchase) => {
    if (!row?.RefID) return alert("Không thể lấy chi tiết phiếu - thiếu RefID");
    try {
      const details = await fetchDetails(row.RefID);
      if (loaiPhieu === "xuat") {
        printPhieuXuat(
          row as MisaOutward,
          details as MisaOutwardDetail[],
          inventorries,
        );
      } else if (loaiPhieu === "nhapkho") {
        printPhieuNhapKho(
          row as MisaInward,
          details as MisaInwardDetail[],
          inventorries,
        );
      } else if (loaiPhieu === "nhaphang") {
        printServiceNhapHang(
          row as MisaPurchase,
          details as MisaPurchaseDetail[],
          inventorries,
        );
      }
    } catch (e) {
      console.error("Lỗi khi lấy chi tiết phiếu để in:", e);
      alert("Có lỗi xảy ra khi lấy chi tiết phiếu. Vui lòng thử lại!");
    }
  };

  const handleExportPDF = async (
    row: MisaOutward | MisaInward | MisaPurchase,
  ) => {
    if (!row?.RefID) return alert("Không thể lấy chi tiết phiếu - thiếu RefID");
    try {
      const details = await fetchDetails(row.RefID);
      await exportPDFService(row, details, loaiPhieu);
    } catch (e) {
      console.error("Lỗi khi xuất file PDF:", e);
      const errorMessage =
        e instanceof Error ? e.message : "Lỗi không xác định";
      alert(`Có lỗi xảy ra khi xuất file PDF: ${errorMessage}`);
    }
  };

  // Thêm handler xuất Excel
  const handleExportExcel = async (
    row: MisaOutward | MisaInward | MisaPurchase,
  ) => {
    if (!row?.RefID) return alert("Không thể lấy chi tiết phiếu - thiếu RefID");
    try {
      const details = await fetchDetails(row.RefID);
      if (loaiPhieu === "nhapkho") {
        await exportExcelServiceNhapKho(
          row as MisaInward,
          details as MisaInwardDetail[],
          loaiPhieu,
          inventorries,
        );
      } else if (loaiPhieu === "xuat") {
        await exportExcelService(row, details, loaiPhieu, inventorries);
      } else if (loaiPhieu === "nhaphang") {
        await exportExcelService(row, details, loaiPhieu, inventorries);
      }
    } catch (error) {
      console.error("Lỗi khi xuất file Excel:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi không xác định";
      alert(`Có lỗi xảy ra khi xuất file Excel: ${errorMessage}`);
    }
  };

  let displayData = misaData;
  if (searchRefNo.trim()) {
    displayData = displayData.filter((row) =>
      row.RefNo?.toLowerCase().includes(searchRefNo.trim().toLowerCase()),
    );
  }
  if (searchJournalMemo.trim()) {
    displayData = displayData.filter((row) =>
      (row.JournalMemo || "")
        .toLowerCase()
        .includes(searchJournalMemo.trim().toLowerCase()),
    );
  }

  // State for inventory map
  const [inventorries, setInventorries] = useState<InventoryItem[]>([]);

  // Gọi API fetchInventory khi vào app
  useEffect(() => {
    // call API fetchInventory và lưu vào state
    const fetchAndSetInventory = async () => {
      try {
        const inventoryResponse = await fetchInventory(misaToken, {
          page: 1,
          limit: 10000,
        });
        setInventorries(inventoryResponse.data ?? []);
      } catch (error) {
        console.error("Lỗi khi lấy dữ liệu hàng tồn kho:", error);
      }
    };
    // chỉ call khi có misaToken
    if (misaToken) {
      fetchAndSetInventory();
    }
  }, [misaToken]);

  const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([]);

  const handleGetDeliveryOrders = async () => {
    if (!misaToken) {
      console.warn("Chưa có MISA token");
      return;
    }
    const formattedFromDate = fromDate?.format("YYYY-MM-DD");
    const formattedToDate = toDate?.format("YYYY-MM-DD");
    setIsLoading(true);
    try {
      const data = await fetchDeliveryOrders(
        misaToken,
        formattedFromDate,
        formattedToDate,
      );

      const filteredData = data.filter(
        (order) => !order.RefNo?.includes("P18"),
      );

      setDeliveryOrders(filteredData);
      console.log("Đơn giao hàng (đã lọc P18):", filteredData);
      exportDeliveryOrdersToExcel(
        filteredData,
        formattedFromDate,
        formattedToDate,
      );
    } catch (error) {
      console.error("Lỗi khi lấy đơn giao hàng:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!currentAccount ? (
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      ) : (
        <div className="App">
          {/* Header AppBar */}
          <AppBar position="sticky" sx={{ marginBottom: 2 }}>
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold" }}>
                MISA - Hệ thống quản lý các phiếu Kho
              </Typography>
              <Typography variant="body2" sx={{ marginRight: 2 }}>
                {currentAccount.username}
                {!isAdmin(currentAccount) && (
                  <span> ({currentAccount.branchName})</span>
                )}
              </Typography>
              <Button
                color="inherit"
                onClick={handleAccountMenuOpen}
                startIcon={<AccountCircleIcon />}
              >
                Tài khoản
              </Button>
              <Menu
                anchorEl={accountMenuAnchor}
                open={Boolean(accountMenuAnchor)}
                onClose={handleAccountMenuClose}
              >
                <MenuItem disabled>
                  {isAdmin(currentAccount)
                    ? "👤 Quản trị viên"
                    : "👤 Người dùng"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleAccountMenuClose();
                    handleLogout();
                  }}
                >
                  <LogoutIcon sx={{ marginRight: 1 }} /> Đăng xuất
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          {/* Branch Selector - Only visible for Admin */}
          {isAdmin(currentAccount) && (
            <Box sx={{ mb: 2, mt: 2, textAlign: "left", pl: "2%" }}>
              <FormControl size="small" sx={{ minWidth: 500 }}>
                <InputLabel>Chi nhánh</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Chi nhánh"
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedBranch(e.target.value);
                    setMisaData([]);
                    setPage(0);
                  }}
                >
                  {filteredBranches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {currentAccount.username === "vip" && (
                <Button
                  variant="contained"
                  onClick={handleGetDeliveryOrders}
                  sx={{ ml: 2 }}
                >
                  Lấy dữ liệu Đơn giao hàng
                </Button>
              )}
            </Box>
          )}

          {/* Filter and Fetch Controls */}
          <Stack
            direction="row"
            spacing={2}
            className="header"
            sx={{ mb: 2, flexWrap: "nowrap", overflow: "hidden" }}
          >
            <Box className="header-date" sx={{ flex: "0 0 20%", minWidth: 0 }}>
              <div className="header-date-label">Từ ngày</div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fromDate}
                  format="DD-MM-YYYY"
                  maxDate={toDate}
                  onChange={handleFromDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box className="header-date" sx={{ flex: "0 0 20%", minWidth: 0 }}>
              <div className="header-date-label">Đến ngày</div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={toDate}
                  format="DD-MM-YYYY"
                  minDate={fromDate}
                  onChange={handleToDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box className="header-date" sx={{ flex: "0 0 20%", minWidth: 0 }}>
              <div className="header-date-label">Loại phiếu</div>
              <FormControl fullWidth size="small">
                <Select
                  value={loaiPhieu}
                  onChange={(
                    e: SelectChangeEvent<"xuat" | "nhapkho" | "nhaphang">,
                  ) => {
                    setLoaiPhieu(
                      e.target.value as "xuat" | "nhapkho" | "nhaphang",
                    );
                    setMisaData([]);
                    setPage(0);
                  }}
                >
                  <MenuItem value="xuat">Xuất kho điều chuyển</MenuItem>
                  <MenuItem value="nhapkho">Nhập kho điều chuyển</MenuItem>
                  <MenuItem value="nhaphang">Nhập hàng NCC</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                flex: "0 0 auto",
                textAlign: "left",
                padding: "30px 0 5px 10px",
                minWidth: 0,
              }}
            >
              <Button
                variant="contained"
                onClick={handleGetMisaData}
                sx={{ height: "40px", whiteSpace: "nowrap" }}
              >
                Lấy dữ liệu MISA
              </Button>
            </Box>
          </Stack>

          {/* Data Table */}
          <div className="content">
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer
                sx={{
                  maxHeight: "65vh",
                  overflowY: "scroll",
                  overflowX: "auto",
                }}
              >
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  style={{
                    width: "100%",
                    minWidth: 500,
                    tableLayout: "fixed",
                  }}
                >
                  <TableHead>
                    {/* Header Row */}
                    <TableRow
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#f8fafc",
                        zIndex: 11,
                      }}
                    >
                      {misaColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            fontWeight: 600,
                            background: "#f8fafc",
                            zIndex: 11,
                            width: column.minWidth,
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Search Row */}
                    <TableRow
                      style={{
                        position: "sticky",
                        top: 40,
                        background: "#f8fafc",
                        zIndex: 10,
                      }}
                    >
                      {misaColumns.map((column) => {
                        if (column.id === "RefNo") {
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              style={{
                                background: "#f8fafc",
                                width: column.minWidth,
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Tìm số phiếu"
                                value={searchRefNo}
                                onChange={(e) => {
                                  setSearchRefNo(e.target.value);
                                  setPage(0);
                                }}
                                style={{
                                  width: "90%",
                                  fontSize: 15,
                                  padding: "6px 10px",
                                  border: "1px solid #bdbdbd",
                                  borderRadius: 4,
                                  background: "#fff",
                                  outline: "none",
                                  transition: "border 0.2s",
                                  boxShadow: "0 1px 2px #0001",
                                }}
                                onFocus={(e) =>
                                  (e.target.style.border =
                                    "1.5px solid #1976d2")
                                }
                                onBlur={(e) =>
                                  (e.target.style.border = "1px solid #bdbdbd")
                                }
                              />
                            </TableCell>
                          );
                        }
                        if (column.id === "JournalMemo") {
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              style={{
                                background: "#f8fafc",
                                width: column.minWidth,
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Tìm diễn giải"
                                value={searchJournalMemo}
                                onChange={(e) => {
                                  setSearchJournalMemo(e.target.value);
                                  setPage(0);
                                }}
                                style={{
                                  width: "90%",
                                  fontSize: 15,
                                  padding: "6px 10px",
                                  border: "1px solid #bdbdbd",
                                  borderRadius: 4,
                                  background: "#fff",
                                  outline: "none",
                                  transition: "border 0.2s",
                                  boxShadow: "0 1px 2px #0001",
                                }}
                                onFocus={(e) =>
                                  (e.target.style.border =
                                    "1.5px solid #1976d2")
                                }
                                onBlur={(e) =>
                                  (e.target.style.border = "1px solid #bdbdbd")
                                }
                              />
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            key={column.id}
                            style={{
                              background: "#f8fafc",
                              width: column.minWidth,
                            }}
                          />
                        );
                      })}
                    </TableRow>
                  </TableHead>

                  {/* Table Body */}
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={misaColumns.length}
                          align="center"
                          style={{
                            padding: "40px 20px",
                            color: "#1976d2",
                          }}
                        >
                          Đang tải dữ liệu...
                        </TableCell>
                      </TableRow>
                    ) : displayData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={misaColumns.length}
                          align="center"
                          style={{
                            padding: "40px 20px",
                            color: "#999",
                          }}
                        >
                          Không có dữ liệu. Vui lòng chọn ngày khác
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayData
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row, index) => (
                          <TableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={`misa-${index}`}
                          >
                            {misaColumns.map((column) => {
                              const value =
                                row[
                                  column.id as keyof (
                                    | MisaOutward
                                    | MisaInward
                                    | MisaPurchase
                                  )
                                ];

                              if (column.id === "RefDate") {
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {value
                                      ? new Date(
                                          value as string,
                                        ).toLocaleDateString("vi-VN")
                                      : ""}
                                  </TableCell>
                                );
                              }

                              if (column.id === "TotalAmount") {
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {value
                                      ? Number(value).toLocaleString("vi-VN")
                                      : ""}
                                  </TableCell>
                                );
                              }

                              if (column.id === "RefNo") {
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    <span
                                      onClick={() => handlePrint(row)}
                                      style={{
                                        color: "#1976d2",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        fontWeight: 600,
                                      }}
                                      title="In phiếu"
                                    >
                                      {(value as string) || ""}
                                    </span>
                                  </TableCell>
                                );
                              }

                              if (column.id === "Action") {
                                return (
                                  <TableCell key={column.id} align="center">
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        onClick={() => handlePrint(row)}
                                        startIcon={<PrintIcon />}
                                      >
                                        In
                                      </Button>
                                      {/* <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={() => handleExportPDF(row)}
                                        startIcon={<PictureAsPdfIcon />}
                                      >
                                        Xuất PDF
                                      </Button> */}
                                      {loaiPhieu !== "nhaphang" && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          color="success"
                                          onClick={() => handleExportExcel(row)}
                                          startIcon={<PictureAsPdfIcon />}
                                        >
                                          Xuất Excel
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                );
                              }

                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {(value as string | number) || ""}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 50, 100]}
                component="div"
                count={displayData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Số dòng / trang:"
              />
            </Paper>
          </div>
        </div>
      )}
    </>
  );
};

export default App;
